import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { 
  CheckCircle2, 
  XCircle, 
  ArrowRight, 
  Search,
  FileCheck,
  Zap,
  TrendingUp,
  Target,
  Shield
} from "lucide-react";
import { MegaMenuNav } from "@/components/MegaMenuNav";
import { Logo } from "@/components/Logo";

const AtsOptimization = () => {
  return (
    <>
      <Helmet>
        <title>ATS Optimization Guide - LoopBaan.ai</title>
        <meta name="description" content="Learn how AI-powered resumes get through Applicant Tracking Systems (ATS) and improve your interview chances. Complete guide to ATS optimization." />
        <meta name="keywords" content="ATS optimization, AI resume, applicant tracking system, resume builder, job search, interview tips, pass ATS, resume keywords" />
        
        <meta property="og:title" content="What Is ATS Optimization? - LoopBaan.ai" />
        <meta property="og:description" content="75% of resumes never make it past applicant tracking systems. Learn how our AI rewrites yours for visibility and interview success." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://yourdomain.com/ats-optimization" />
        
        <link rel="canonical" href="https://yourdomain.com/ats-optimization" />
      </Helmet>
      
      <div className="min-h-screen bg-background">
        {/* Navigation */}
      <MegaMenuNav />

      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 md:py-32">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-accent/5 to-background" />
        <div className="container relative mx-auto px-4">
          <div className="mx-auto max-w-4xl text-center">
            <Badge className="mb-6 bg-primary/10 text-primary hover:bg-primary/20">
              ATS Optimization Guide
            </Badge>
            <h1 className="mb-6 text-5xl font-bold leading-tight tracking-tight md:text-6xl">
              Why ATS Optimization Matters
            </h1>
            <p className="mb-10 text-xl text-muted-foreground md:text-2xl">
              Learn how AI ensures your resume passes hiring filters and lands in front of recruiters.
            </p>
            <Link to="/signup">
              <Button size="lg" className="gap-2 text-lg">
                Get My ATS-Optimized Resume <ArrowRight className="h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* What is ATS */}
      <section className="border-t bg-muted/30 py-20">
        <div className="container mx-auto px-4">
          <div className="mb-16 text-center">
            <h2 className="mb-4 text-4xl font-bold">What is an ATS?</h2>
            <p className="mx-auto max-w-2xl text-lg text-muted-foreground">
              An Applicant Tracking System (ATS) is software that scans your resume for relevance before a recruiter ever sees it. 
              If your resume isn't formatted properly, it's invisible to 90% of hiring teams.
            </p>
          </div>

          {/* ATS Logos */}
          <div className="mb-12 grid grid-cols-2 gap-8 md:grid-cols-4">
            {[
              { name: "Workday", icon: Shield },
              { name: "Greenhouse", icon: Target },
              { name: "Lever", icon: TrendingUp },
              { name: "Taleo", icon: FileCheck }
            ].map((ats) => (
              <Card key={ats.name} className="flex flex-col items-center justify-center gap-3 p-6">
                <ats.icon className="h-10 w-10 text-primary" />
                <span className="font-semibold">{ats.name}</span>
              </Card>
            ))}
          </div>

          {/* ATS Flow Diagram */}
          <div className="mx-auto max-w-4xl">
            <Card className="p-8">
              <div className="flex items-center justify-between gap-4">
                <div className="flex flex-col items-center gap-2 text-center">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                    <FileCheck className="h-8 w-8 text-primary" />
                  </div>
                  <span className="text-sm font-medium">Resume Submitted</span>
                </div>
                <ArrowRight className="h-6 w-6 text-muted-foreground" />
                <div className="flex flex-col items-center gap-2 text-center">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-accent/10">
                    <Search className="h-8 w-8 text-accent" />
                  </div>
                  <span className="text-sm font-medium">ATS Scans</span>
                </div>
                <ArrowRight className="h-6 w-6 text-muted-foreground" />
                <div className="flex flex-col items-center gap-2 text-center">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
                    <XCircle className="h-8 w-8 text-destructive" />
                  </div>
                  <span className="text-sm font-medium">75% Rejected</span>
                </div>
              </div>
            </Card>
            <p className="mt-4 text-center text-sm text-muted-foreground">
              75% of resumes never make it past this initial ATS scan
            </p>
          </div>
        </div>
      </section>

      {/* How Resumes Get Rejected */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="mb-16 text-center">
            <h2 className="mb-4 text-4xl font-bold">How Resumes Get Rejected (and How We Fix It)</h2>
            <p className="mx-auto max-w-2xl text-lg text-muted-foreground">
              ATS systems scan your resume for relevance before a recruiter ever sees it.
            </p>
          </div>

          <div className="mx-auto max-w-5xl">
            <div className="grid gap-8 md:grid-cols-2">
              {/* Common Mistakes Column */}
              <div>
                <h3 className="mb-6 flex items-center gap-2 text-2xl font-bold">
                  <XCircle className="h-6 w-6 text-destructive" />
                  Common ATS Mistakes
                </h3>
                <div className="space-y-4">
                  {[
                    { title: "Complex Tables & Graphics", desc: "ATS can't read fancy designs or multi-column layouts" },
                    { title: "Missing Keywords", desc: "Resume doesn't match job description terminology" },
                    { title: "Incorrect Formatting", desc: "Headers, fonts, and sections confuse the parser" },
                    { title: "Generic Content", desc: "One-size-fits-all resumes get filtered out" }
                  ].map((mistake, i) => (
                    <Card key={i} className="border-destructive/20 p-4">
                      <h4 className="mb-1 font-semibold">{mistake.title}</h4>
                      <p className="text-sm text-muted-foreground">{mistake.desc}</p>
                    </Card>
                  ))}
                </div>
              </div>

              {/* Our Fix Column */}
              <div>
                <h3 className="mb-6 flex items-center gap-2 text-2xl font-bold">
                  <CheckCircle2 className="h-6 w-6 text-primary" />
                  Our AI Fix
                </h3>
                <div className="space-y-4">
                  {[
                    { title: "Clean, ATS-Friendly Layout", desc: "Simple formatting that every ATS can parse correctly" },
                    { title: "Keyword Optimization", desc: "AI analyzes job descriptions and adds relevant terms naturally" },
                    { title: "Proper Structure", desc: "Standardized sections with clear headers ATS expects" },
                    { title: "Tailored for Each Role", desc: "AI rewrites bullet points to match specific job requirements" }
                  ].map((fix, i) => (
                    <Card key={i} className="border-primary/20 p-4">
                      <h4 className="mb-1 font-semibold">{fix.title}</h4>
                      <p className="text-sm text-muted-foreground">{fix.desc}</p>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Optimization Process */}
      <section className="border-t bg-muted/30 py-20">
        <div className="container mx-auto px-4">
          <div className="mb-16 text-center">
            <h2 className="mb-4 text-4xl font-bold">Our AI Optimization Process</h2>
            <p className="mx-auto max-w-2xl text-lg text-muted-foreground">
              Our AI engine uses keyword analysis and data-backed formatting to make sure you're never filtered out again.
            </p>
          </div>

          <div className="mx-auto max-w-4xl">
            <div className="grid gap-6 md:grid-cols-2">
              {[
                {
                  icon: Search,
                  title: "1. Parse the Job Description",
                  desc: "AI analyzes the job posting to identify key requirements, skills, and terminology"
                },
                {
                  icon: Target,
                  title: "2. Identify Missing Keywords",
                  desc: "Compares your experience against job requirements to find gaps"
                },
                {
                  icon: Zap,
                  title: "3. Rewrite Bullet Points",
                  desc: "Transforms your achievements into ATS-friendly language with measurable results"
                },
                {
                  icon: FileCheck,
                  title: "4. Format for 100% ATS Readability",
                  desc: "Applies clean, standardized formatting that passes all major ATS platforms"
                }
              ].map((step) => (
                <Card key={step.title} className="p-6">
                  <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                    <step.icon className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="mb-2 text-xl font-semibold">{step.title}</h3>
                  <p className="text-muted-foreground">{step.desc}</p>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Proof + Trust */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-4xl">
            <Card className="overflow-hidden border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-accent/5">
              <div className="p-12 text-center">
                <div className="mb-6">
                  <div className="text-6xl font-bold text-primary">80%</div>
                  <p className="mt-2 text-xl text-muted-foreground">
                    of users who used our ATS-optimized resume received interviews within 2 weeks
                  </p>
                </div>
                

                {/* Testimonials */}
                <div className="grid gap-6 md:grid-cols-2">
                  {[
                    {
                      quote: "I got 3 interviews in my first week using the AI resume. The keyword optimization made all the difference.",
                      author: "Sarah M., Software Engineer"
                    },
                    {
                      quote: "Finally, a resume that actually gets past the ATS. I was shocked how fast recruiters started reaching out.",
                      author: "Michael T., Marketing Manager"
                    }
                  ].map((testimonial, i) => (
                    <Card key={i} className="p-6 text-left">
                      <p className="mb-3 italic text-muted-foreground">"{testimonial.quote}"</p>
                      <p className="font-semibold">— {testimonial.author}</p>
                    </Card>
                  ))}
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="border-t bg-gradient-to-br from-primary via-primary to-accent py-20 text-primary-foreground">
        <div className="container mx-auto px-4 text-center">
          <h2 className="mb-4 text-4xl font-bold md:text-5xl">
            Get an ATS-Optimized Resume Today
          </h2>
          <p className="mb-8 text-xl opacity-90">
            Start free. Get interviews. Land your dream job.
          </p>
          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link to="/pricing">
              <Button size="lg" variant="secondary" className="gap-2 text-lg">
                View Pricing <ArrowRight className="h-5 w-5" />
              </Button>
            </Link>
            <Link to="/samples">
              <Button size="lg" variant="outline" className="border-white bg-white/10 text-white hover:bg-white/20">
                See Examples
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t bg-muted/30 py-12">
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
            <Link to="/" className="flex items-center gap-2">
              <Logo className="h-12 w-12" />
              <span className="font-semibold">LoopBaan.ai</span>
            </Link>
            <div className="flex gap-6 text-sm text-muted-foreground">
              <Link to="/contact" className="hover:text-foreground">Contact</Link>
              <Link to="/ats-optimization" className="hover:text-foreground">ATS Guide</Link>
              <Link to="/terms" className="hover:text-foreground">Terms</Link>
              <Link to="/privacy" className="hover:text-foreground">Privacy</Link>
            </div>
          </div>
          <div className="mt-8 text-center text-sm text-muted-foreground">
            © 2025 LoopBaan.ai. All rights reserved.
          </div>
        </div>
      </footer>

      </div>
    </>
  );
};

export default AtsOptimization;
